import React from 'react';

import { Box, FormLabel, MenuItem, Stack, TextField } from '@mui/material';

import { getRoomCategoryOptionsFromSchema, useRoomTypesSchema } from '~/hooks/apis/accommodation/useRoomTypesSchema';

import type { OfferBuilderRoom } from '../types';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
}

const SOURCE_OPTIONS: Array<string> = ['cm-sync', 'admin'];

function getCategoryOptions(category: string, contractCategoryOptions: Array<string>) {
  return category ? Array.from(new Set([category].concat(contractCategoryOptions))) : contractCategoryOptions;
}

function getSource(room: OfferBuilderRoom): string {
  if (room.source) {
    return room.source;
  }

  if (room.sourceLabel === 'cm-sync' || room.sourceLabel === 'admin') {
    return room.sourceLabel;
  }

  if (room.has_live_cm_data === true) {
    return 'cm-sync';
  }

  if (room.has_live_cm_data === false) {
    return 'admin';
  }

  return '';
}

function parseAvailabilityThreshold(value: string) {
  return value === '' ? null : value;
}

export default function ChannelListingTab({ onChange, room }: Props) {
  const { data: roomTypesSchema } = useRoomTypesSchema({ propertyId: room.property_id });
  const category = room.category?.trim() ? room.category : '';
  const categoryOptions = getCategoryOptions(category, getRoomCategoryOptionsFromSchema(roomTypesSchema));
  const source = getSource(room);
  const sourceOptions = source && !SOURCE_OPTIONS.includes(source) ? [source, ...SOURCE_OPTIONS] : SOURCE_OPTIONS;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      <Stack spacing={1}>
        <FormLabel>Low availability threshold</FormLabel>
        <TextField
          type="number"
          size="small"
          value={room.availability_threshold ?? ''}
          onChange={(event) => onChange({ availability_threshold: parseAvailabilityThreshold(event.target.value) })}
        />
      </Stack>

      <Stack spacing={1}>
        <FormLabel>OTA room type code</FormLabel>
        <TextField
          size="small"
          value={room.room_type_code ?? ''}
          onChange={(event) => onChange({ room_type_code: event.target.value || null })}
        />
      </Stack>

      <Stack spacing={1}>
        <FormLabel>Category</FormLabel>
        <TextField
          select
          size="small"
          value={category}
          onChange={(event) => onChange({ category: event.target.value || null })}
        >
          <MenuItem value="">Not specified</MenuItem>
          {categoryOptions.map((categoryOption) => (
            <MenuItem key={categoryOption} value={categoryOption}>
              {categoryOption}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack spacing={1}>
        <FormLabel>Source</FormLabel>
        <TextField
          select
          size="small"
          value={source}
          onChange={(event) =>
            onChange({
              has_live_cm_data: event.target.value ? event.target.value === 'cm-sync' : null,
              source: (event.target.value || null) as OfferBuilderRoom['source'],
            })
          }
        >
          <MenuItem value="">Not specified</MenuItem>
          {sourceOptions.map((sourceOption) => (
            <MenuItem key={sourceOption} value={sourceOption}>
              {sourceOption}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Box>
  );
}
